/**
 * WalletTable Component
 *
 * Displays a searchable, server-side paginated table of Hedera wallets
 * for the User Balances page.
 * Search is delegated to the parent (which calls the server).
 * Pagination is server-driven, the component receives one page of data.
 *
 * @component
 */

import React from "react";
import { WALLETS_PER_PAGE } from "./constants";
import {
    commonStyles,
    colors,
    paginationStyles,
    buttonStyles,
} from "../AgenticPaymentTransactionHistory/styles";
import { calculatePaginationRange } from "../AgenticPaymentTransactionHistory/utils";

// ============================================================================
// Sub-components
// ============================================================================

const WalletPagination = ({
    currentPage,
    totalPages,
    onPageChange,
    totalItems,
}) => {
    if (totalPages <= 1) return null;

    const pages = calculatePaginationRange(currentPage, totalPages);
    const startItem = (currentPage - 1) * WALLETS_PER_PAGE + 1;
    const endItem = Math.min(currentPage * WALLETS_PER_PAGE, totalItems);

    return (
        <div style={paginationStyles.container}>
            <span style={paginationStyles.info}>
                Showing {startItem}-{endItem} of {totalItems} wallets
            </span>
            <div style={paginationStyles.nav}>
                <button
                    type="button"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                        ...paginationStyles.navButton,
                        background:
                            currentPage === 1 ? colors.gray300 : colors.primary,
                        color:
                            currentPage === 1 ? colors.gray500 : colors.white,
                        cursor:
                            currentPage === 1 ? "not-allowed" : "pointer",
                    }}
                >
                    ← Prev
                </button>
                {pages.map((page) => (
                    <button
                        key={page}
                        type="button"
                        onClick={() => onPageChange(page)}
                        style={{
                            ...paginationStyles.pageButton,
                            background:
                                page === currentPage
                                    ? colors.accent
                                    : colors.white,
                            color:
                                page === currentPage
                                    ? colors.white
                                    : colors.gray700,
                            border:
                                page === currentPage
                                    ? "none"
                                    : `1px solid ${colors.border}`,
                            fontWeight: page === currentPage ? 700 : 400,
                        }}
                    >
                        {page}
                    </button>
                ))}
                <button
                    type="button"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                        ...paginationStyles.navButton,
                        background:
                            currentPage === totalPages
                                ? colors.gray300
                                : colors.primary,
                        color:
                            currentPage === totalPages
                                ? colors.gray500
                                : colors.white,
                        cursor:
                            currentPage === totalPages
                                ? "not-allowed"
                                : "pointer",
                    }}
                >
                    Next →
                </button>
            </div>
        </div>
    );
};

// ============================================================================
// Main Component
// ============================================================================

const WalletTable = ({
    wallets,
    walletsLoading,
    totalCount,
    currentPage,
    totalPages,
    searchQuery,
    onSearchChange,
    onSearchSubmit,
    onSearchClear,
    onPageChange,
    onViewBalances,
}) => {
    // Loading state
    if (walletsLoading && wallets.length === 0) {
        return (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <div
                    style={{
                        width: "40px",
                        height: "40px",
                        border: `3px solid ${colors.border}`,
                        borderTopColor: colors.primary,
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                        margin: "0 auto 16px",
                    }}
                />
                <p style={{ color: colors.gray600, fontSize: "14px" }}>
                    Loading wallets...
                </p>
            </div>
        );
    }

    return (
        <div>
            {/* Section Header */}
            <div style={commonStyles.sectionHeader}>
                <h2
                    style={{
                        margin: 0,
                        color: colors.white,
                        fontSize: "20px",
                        fontWeight: 600,
                    }}
                >
                    Hedera Wallets
                </h2>
                <span
                    style={{
                        background: "rgba(255,255,255,0.15)",
                        color: colors.white,
                        padding: "6px 14px",
                        borderRadius: "20px",
                        fontSize: "13px",
                        fontWeight: 600,
                    }}
                >
                    {totalCount} wallet{totalCount !== 1 ? "s" : ""} total
                </span>
            </div>

            {/* Search */}
            <div style={commonStyles.filterBar}>
                <div style={{ display: "flex", gap: "8px", flex: 1, maxWidth: "500px", alignItems: "center" }}>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                onSearchSubmit();
                            }
                        }}
                        placeholder="Search by email, name, or username..."
                        style={{ ...commonStyles.input, flex: 1 }}
                    />
                    <button
                        type="button"
                        onClick={onSearchSubmit}
                        disabled={walletsLoading}
                        style={{
                            padding: "10px 20px",
                            background: colors.primary,
                            color: colors.white,
                            border: "none",
                            borderRadius: "4px",
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: walletsLoading ? "not-allowed" : "pointer",
                            opacity: walletsLoading ? 0.7 : 1,
                            whiteSpace: "nowrap",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                        }}
                    >
                        {walletsLoading ? (
                            <>
                                <span
                                    style={{
                                        width: "14px",
                                        height: "14px",
                                        border: `2px solid rgba(255,255,255,0.3)`,
                                        borderTopColor: colors.white,
                                        borderRadius: "50%",
                                        animation: "spin 0.8s linear infinite",
                                        display: "inline-block",
                                    }}
                                />
                                Searching...
                            </>
                        ) : (
                            <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8" />
                                    <path d="M21 21l-4.35-4.35" />
                                </svg>
                                Search
                            </>
                        )}
                    </button>
                    {searchQuery && (
                        <button
                            type="button"
                            onClick={onSearchClear}
                            disabled={walletsLoading}
                            title="Clear search"
                            style={{
                                width: "36px",
                                height: "36px",
                                padding: 0,
                                background: colors.gray300,
                                color: colors.gray700,
                                border: "none",
                                borderRadius: "4px",
                                fontSize: "16px",
                                fontWeight: 700,
                                lineHeight: 1,
                                cursor: walletsLoading ? "not-allowed" : "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            ✕
                        </button>
                    )}
                </div>
                <span
                    style={{
                        ...commonStyles.badge,
                        background: colors.accent,
                        color: colors.white,
                    }}
                >
                    Showing {wallets.length} of {totalCount}
                </span>
            </div>

            {/* Table */}
            {wallets.length === 0 && !walletsLoading ? (
                <div className="blank_slate">
                    <p>
                        {searchQuery
                            ? "No wallets match your search"
                            : "No Hedera wallets found"}
                    </p>
                </div>
            ) : (
                <div style={{ ...commonStyles.panel, marginBottom: 0 }}>
                    <div className="index_table_container">
                        <table className="index_table">
                            <thead>
                                <tr>
                                    <th className="col">ID</th>
                                    <th className="col">Email</th>
                                    <th className="col">Name</th>
                                    <th className="col">Wallet Address</th>
                                    <th className="col">Status</th>
                                    <th className="col">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {wallets.map((wallet, idx) => (
                                    <tr
                                        key={wallet.id}
                                        className={
                                            idx % 2 === 0 ? "even" : "odd"
                                        }
                                    >
                                        <td className="col">
                                            {wallet.id || "-"}
                                        </td>
                                        <td className="col">
                                            {wallet.email || "-"}
                                        </td>
                                        <td className="col">
                                            {wallet.name || "-"}
                                        </td>
                                        <td className="col">
                                            <code>{wallet.address}</code>
                                        </td>
                                        <td className="col">
                                            <span
                                                className={`status_tag ${wallet.status}`}
                                            >
                                                {wallet.status || "active"}
                                            </span>
                                        </td>
                                        <td className="col">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    onViewBalances(wallet)
                                                }
                                                style={
                                                    buttonStyles.viewHistory
                                                }
                                                onMouseOver={(e) =>
                                                    Object.assign(
                                                        e.currentTarget.style,
                                                        buttonStyles.viewHistoryHover,
                                                    )
                                                }
                                                onMouseOut={(e) =>
                                                    Object.assign(
                                                        e.currentTarget.style,
                                                        buttonStyles.viewHistoryDefault,
                                                    )
                                                }
                                            >
                                                <svg
                                                    width="14"
                                                    height="14"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
                                                    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
                                                    <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
                                                </svg>
                                                View Balance
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <WalletPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={onPageChange}
                        totalItems={totalCount}
                    />
                </div>
            )}
        </div>
    );
};

export default WalletTable;
